import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import type { Extensions, JSONContent } from '@tiptap/react';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import type { EditorView } from '@tiptap/pm/view';
import { RICH_TEXT_EXTENSIONS } from 'components/richText';
import { normalizeEmptyHtml } from 'utils/richText';

export const useRichTextField = ({
  value,
  onChange,
  placeholder,
  attributes,
  onFocus,
  onBlur,
  onKeyDown,
  extraExtensions,
  parseValue,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  attributes?: Record<string, string>;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent) => boolean;
  extraExtensions?: Extensions;
  parseValue?: (html: string) => JSONContent;
}) => {
  const onKeyDownRef = useRef(onKeyDown);
  useEffect(() => {
    onKeyDownRef.current = onKeyDown;
  }, [onKeyDown]);

  const extensions = useMemo(
    () => [
      ...RICH_TEXT_EXTENSIONS,
      UndoRedo,
      Placeholder.configure({ placeholder }),
      ...(extraExtensions ?? []),
    ],
    [placeholder, extraExtensions]
  );

  const toContent = useCallback(
    (html: string) => (parseValue ? parseValue(html) : html),
    [parseValue]
  );

  const initialContent = useMemo(() => toContent(value), [value, toContent]);

  const editorProps = useMemo(
    () => ({
      attributes: {
        ...attributes,
        'data-ph-no-autocapture': 'true',
      },
      handleKeyDown: (_view: EditorView, event: KeyboardEvent) =>
        onKeyDownRef.current?.(event) ?? false,
    }),
    [attributes]
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editorProps,
    onUpdate: ({ editor }) => onChange(normalizeEmptyHtml(editor.getHTML())),
    onFocus: () => onFocus?.(),
    onBlur: () => onBlur?.(),
  });

  useEffect(() => {
    // Skip while the editor holds focus — including when a node-view input (a free-text slot) has
    // DOM focus, which clears editor.isFocused but must not let an incoming value re-render the doc
    // and clobber what is being typed.
    if (editor.isFocused || editor.view.dom.contains(document.activeElement)) return;
    if (normalizeEmptyHtml(editor.getHTML()) !== value) {
      editor
        .chain()
        .setMeta('addToHistory', false)
        .setContent(toContent(value), { emitUpdate: false })
        .run();
    }
  }, [value, editor, toContent]);

  return { editor };
};
